/**
 * Shared fim-sentinel contract: the entry shape and the range-scoring
 * response, used by both the Dossiers sentinel views (pages/claims/sentinels)
 * and the Process Data chart overlay (features/timeSeries). Design/run/result
 * types stay in pages/claims/sentinels/types.
 */

export const SENTINEL_ENTRY_TYPE = 'fim-sentinel'

export type SentinelRunStatus = 'running' | 'done' | 'error'
/** `preview` marks ephemeral job-handoff scratch entries; never listed. */
export type SentinelMode = 'target' | 'multitag' | 'experiment' | 'preview'

export const TARGET_METHOD_KEYS = [
  'target_mlp_none',
  'target_mlp_driver',
  'target_mlp_driver_own',
  'target_knn',
] as const

export type SentinelTargetMethodKey = (typeof TARGET_METHOD_KEYS)[number]

export interface SentinelEntryData {
  name: string
  run_id: string
  summary?: {
    status?: SentinelRunStatus
    mode?: SentinelMode
    error?: string
    requested_at?: string
    target?: string
    drivers?: number
    tags?: number
    families?: number
    start_date?: string
    end_date?: string
    interval_minutes?: number
    rows?: number
    split_date?: string
  }
  files?: { request?: string; result?: string; models?: string }
}

export interface SentinelLoadedEntry {
  entry: {
    id: string
    type: string
    data: SentinelEntryData
    tags: Record<string, string>
    updated_at: string
  }
}

/** Longest scorable range; matches the service-side guard. */
export const MAX_SCORE_DAYS = 14

/** One method's output of range scoring (native-interval series). */
export interface ScoreRecentMethod {
  band_k: number
  expected: Record<string, (number | null)[]>
  residual: Record<string, (number | null)[]>
  err_scale: Record<string, number | null>
  residual_band: Record<string, number | null>
}

/** Result of scoring a date range with the sentinel's saved models. */
export interface ScoreRecentResponse {
  ok: boolean
  error?: string
  entry_id?: string
  mode?: SentinelMode
  target?: string
  drivers?: string[]
  window?: {
    start: string
    end: string
    interval_minutes: number
  }
  trained_split_date?: string
  model_created_at?: string
  ts?: string[]
  tags?: string[]
  actual?: Record<string, (number | null)[]>
  methods?: Record<string, ScoreRecentMethod>
}
